import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import axios from "axios";
import { useAuthStore } from "../store";
import { plural, formatPln } from "../utils/paymentFormat";
import "../assets/style/PendingSignupsBanner.css";

const API = process.env.REACT_APP_BACKEND_ADDRESS;

/**
 * In-app notification for the logged-in user's DUE online-payable balance —
 * past unpaid lessons only. Future admin-signups awaiting payment are NOT
 * surfaced here (they stay payable on `/moje-platnosci` but must not nudge the
 * user). Cash-on-site lessons (a cash-only lesson or the user's explicit
 * "Zapłać na miejscu" choice) are likewise excluded — they are settled in
 * person. Renders a fixed warning bar under the site header.
 *
 * When the user holds a LIVE pass that can cover the due lessons, the banner
 * settles them in place with one click (confirm dialog → `use-pass-auto`), so
 * the user never has to visit the cluttered payments page. The other modes
 * (renew an expired pass / pay online) deep-link into `/moje-platnosci`.
 *
 * Renders nothing when the user is logged out, has no due online balance, or
 * dismissed it for this page view. The account-icon badge remains the
 * persistent indicator.
 */
const PendingSignupsBanner = () => {
  const navigate = useNavigate();
  const { isAuthenticated, pendingSignups, user, fetchPendingSignups } =
    useAuthStore((state) => ({
      isAuthenticated: state.isAuthenticated,
      pendingSignups: state.pendingSignups,
      user: state.user,
      fetchPendingSignups: state.fetchPendingSignups,
    }));

  const [dismissed, setDismissed] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [covering, setCovering] = useState(false);
  const [coverError, setCoverError] = useState(null);
  const [coverSuccess, setCoverSuccess] = useState(null);

  // Pin the banner just below the fixed site header, tracking its height
  // across breakpoints / orientation changes.
  useEffect(() => {
    const measure = () => {
      const header = document.getElementById("header");
      setHeaderOffset(header ? header.offsetHeight : 0);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Only the DUE online-payable balance drives the banner. Past unpaid lessons
  // always count; future admin-signups are excluded UNLESS they are a pass-
  // renewal nudge (`pass_renewable`), which the store keeps in the `due` bucket
  // so a "karnetowiec" with a lapsed pass is reminded before the lesson. Cash-
  // on-site lessons are always excluded so we never nudge online payment for
  // something settled in person.
  const count = pendingSignups?.due?.online_count || 0;
  const total = pendingSignups?.due?.online_total || 0;
  // When any due online lesson is a pass-renewal nudge (the user is a
  // "karnetowiec" for that category whose pass expired), switch the copy/CTA to
  // encourage renewing the pass rather than a one-off payment.
  const hasRenewable = pendingSignups?.due?.has_renewable || false;
  // Highest-priority nudge: the user already holds a LIVE pass that can cover a
  // due lesson, so they can settle it for free with one click — handled inline
  // here. Takes precedence over both renewal and paid messaging.
  const hasUsablePass = pendingSignups?.due?.has_usable_pass || false;

  // A fresh success message can outlive the balance dropping to zero, so show
  // it even once `count === 0`. Otherwise the banner only renders when there is
  // a due balance to act on.
  if (!isAuthenticated || dismissed) {
    return null;
  }
  if (count === 0 && !coverSuccess) {
    return null;
  }

  const handleSettle = () => {
    navigate("/moje-platnosci");
  };

  // Pass-coverable: settle inline with one click; other modes deep-link to the
  // full payments page. If auth has rehydrated `isAuthenticated` but the
  // Firebase `user` is not ready yet (or any non-coverable mode), fall back to
  // the payments page rather than opening a dialog we cannot submit.
  const handlePrimaryAction = () => {
    if (hasUsablePass && user) {
      setCoverError(null);
      setConfirmOpen(true);
    } else {
      handleSettle();
    }
  };

  const handleConfirmCover = async () => {
    if (!user) {
      // Auth not ready — don't strand the user in a dead dialog.
      setConfirmOpen(false);
      handleSettle();
      return;
    }
    setCovering(true);
    setCoverError(null);
    try {
      const idToken = await user.getIdToken();
      const resp = await axios.post(`${API}/user/me/owed/use-pass-auto`, null, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      const settled = resp?.data?.settled_count || 0;
      const remaining = resp?.data?.remaining_count ?? 0;
      setConfirmOpen(false);
      // Refresh the due balance so the banner/badge reflect the new state.
      await fetchPendingSignups();
      // Only show the terminal "success" (which hides the CTA) when nothing is
      // left to settle. On a partial cover, leave the banner actionable so the
      // refreshed balance re-renders with a renew/pay CTA for the remainder.
      if (remaining === 0) {
        setCoverSuccess(
          `Karnet pokrył ${settled} ${plural(
            settled,
            "termin",
            "terminy",
            "terminów"
          )}.`
        );
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setCoverError(`Nie udało się pokryć karnetem: ${msg}`);
    } finally {
      setCovering(false);
    }
  };

  // Precedence: cover-with-existing-pass (free, 1 click) > renew-expired-pass
  // (must buy) > plain online payment.
  let buttonLabel;
  if (hasUsablePass) {
    buttonLabel = "Pokryj karnetem";
  } else if (hasRenewable) {
    buttonLabel = "Odnów karnet";
  } else {
    buttonLabel = "Opłać teraz";
  }

  return (
    <div
      className="pending-signups-banner"
      style={{ top: `${headerOffset}px` }}
    >
      <Alert
        severity={coverSuccess ? "success" : "warning"}
        className="pending-signups-banner__alert"
        onClose={() => setDismissed(true)}
        action={
          coverSuccess ? null : (
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              className="pending-signups-banner__button"
              onClick={handlePrimaryAction}
            >
              {buttonLabel}
            </Button>
          )
        }
      >
        {coverSuccess ? (
          coverSuccess
        ) : hasUsablePass ? (
          <>
            Masz karnet, który pokryje {count}{" "}
            {plural(count, "termin", "terminy", "terminów")} do opłacenia.
            Pokryj zaległość karnetem bez dodatkowych kosztów.
          </>
        ) : hasRenewable ? (
          <>
            Twój karnet wygasł. Masz {count}{" "}
            {plural(count, "termin", "terminy", "terminów")} do opłacenia na
            kwotę <strong>{formatPln(total)}</strong>. Odnów karnet, aby je
            pokryć.
          </>
        ) : (
          <>
            Masz {count}{" "}
            {plural(
              count,
              "nieopłacony termin",
              "nieopłacone terminy",
              "nieopłaconych terminów"
            )}{" "}
            zajęć na kwotę <strong>{formatPln(total)}</strong>. Przejdź, aby
            uregulować płatność.
          </>
        )}
      </Alert>

      <Dialog
        open={confirmOpen}
        onClose={() => (covering ? null : setConfirmOpen(false))}
        aria-labelledby="cover-with-pass-dialog-title"
      >
        <DialogTitle id="cover-with-pass-dialog-title">
          Pokryć zaległości karnetem?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Twoje zaległe zajęcia ({count}{" "}
            {plural(count, "termin", "terminy", "terminów")}) zostaną pokryte z
            Twojego karnetu — wykorzystasz wejścia, bez dodatkowych kosztów.
          </DialogContentText>
          {coverError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {coverError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={covering}>
            Anuluj
          </Button>
          <Button
            onClick={handleConfirmCover}
            variant="contained"
            disabled={covering}
            startIcon={
              covering ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Pokryj karnetem
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PendingSignupsBanner;
